// @ts-ignore
import { FunctionComponent, useEffect, useState } from 'react';
import { Layout } from 'components/Layout/Layout';
import Head from 'next/head';
import { useMediaQuery } from 'react-responsive';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import styled, { css } from 'styled-components';
import { Header } from 'components/Layout/Header';
import { FlexWrapper, FlexBox } from 'components/Layout/Flex';
import { Text } from 'components/Typography/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Link from 'next/link';
import { ActionButton } from '../../components/Buttons/ActionButton';
import { Spacer } from '../../components/Layout/Spacer';
import { Consts as StyleConsts } from '../../styles/consts';
import { Footer } from '../../components/Layout/Footer';

const testimonials = [
  {
    text: 'Everything I experienced throughout this competition will stay with me for a long time, and will shape how I think and make decisions.',
    author: 'Annamaria Szelics',
    position: 'The Earth Prize Mentor',
  },
  {
    text: "The Earth Prize came in just at the time we were saying that we don't want to focus on the gloom and doom, we want to focus on hope and solutions.",
    author: 'Pauline Herbommez',
    position: 'Teacher at Eton College',
  },
  {
    text: 'The prize challenges you to become a better teacher and learning facilitator, and for me, that is worth a million dollars right there.',
    author: 'Laurence Myers',
    position: 'Teacher at American School of Dubai',
  },
  {
    text: 'Not only has the competition allowed us to develop our idea to great depths, but it also allowed me to meet with great experts and extremely amiable team members.',
    author: 'Huyen Luong',
    position: 'The Earth Prize Participant',
  },
  {
    text: 'This competition will allow you to see your true potential.',
    author: 'Rachelle Armstrong',
    position: 'The Earth Prize Participant',
  },
];

const Index: FunctionComponent = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const mobile = useMediaQuery({
    query: `(max-width: ${StyleConsts.mobileBreakPoint})`,
  });
  const desktop = useMediaQuery({
    query: `(min-width: ${StyleConsts.desktopBreakPoint})`,
  });
  const [isDesktop, setDesktop] = useState(false);
  const [isMobile, setMobile] = useState(false);

  useEffect(() => {
    setMobile(mobile);
  }, [mobile]);

  useEffect(() => {
    setDesktop(desktop);
  }, [desktop]);

  return (
    <Layout>
      <Head>
        <title>The Earth Prize - Become a Partner</title>
        <meta
          name="description"
          content="Support The Earth Prize such as by sponsoring the prize, the creation of additional learning content, the organization of the Awards Ceremony and other events, and the traveling costs of winning teams to conferences."
        />
        <meta
          property="og:title"
          content="The Earth Prize - Become a Partner"
        />
        <meta
          property="og:description"
          content="Support The Earth Prize such as by sponsoring the prize, the creation of additional learning content, the organization of the Awards Ceremony and other events, and the traveling costs of winning teams to conferences."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <Header withoutLogin />
      <BecomePartnerWrapper>
        {!isMobile && (
          <Quote>
            <Text color="black">
              “We are eager to join forces with companies which can champion the
              future generation of environmental problem solvers and leaders,
              and help bring some of their sustainability projects to life”
              <br />
              <br />
              <br />
              <b>
                Angela McCarthy, CEO of
                <br />
                The Earth Foundation
              </b>
            </Text>
          </Quote>
        )}
        <FlexWrapperStyled>
          <FlexBox justify="flex-start">
            {isDesktop || isMobile ? (
              <Spacer height={isMobile ? 10 : 110} />
            ) : (
              <Spacer height={40} />
            )}
            <Label>PARTNERSHIPS</Label>
            <MainTitle>What is your path to sustainability?</MainTitle>
            {isMobile && (
              <Text color="black">
                “We are eager to join forces with companies which can champion
                the future generation of environmental problem solvers and
                leaders, and help bring some of their sustainability projects to
                life”
                <br />
                <br />
                <br />
                <b>
                  Peter McGarry, Founder of
                  <br /> The Earth Foundation
                </b>
              </Text>
            )}
          </FlexBox>
          <FlexBox justify="flex-start">
            {isDesktop || isMobile ? (
              <Spacer height={isMobile ? 10 : 170} />
            ) : (
              <Spacer height={100} />
            )}
            <iframe
              title="vimeo-player"
              src="https://player.vimeo.com/video/759429758?h=5d845b7cf8"
              width="100%"
              height={isMobile ? '100%' : '300px'}
              frameBorder="0"
              allowFullScreen
            />
          </FlexBox>
        </FlexWrapperStyled>
        <PartnershipWrapper>
          <Label>PARTNERSHIPS</Label>
          <SecondaryTitle>
            Partnering with The Earth Prize offers you the opportunity to:
          </SecondaryTitle>
        </PartnershipWrapper>
        <OpportunitiesWrapper>
          <Opportunity>
            <div>
              <OpportunityIcon icon="/images/opportunity1.svg" />
              <SmallTitle>Display your path to sustainability</SmallTitle>
            </div>
            <Text>
              Visible demonstration of corporate social responsibility, positive
              media coverage and publicity.
            </Text>
          </Opportunity>
          <Opportunity>
            <div>
              <OpportunityIcon icon="/images/opportunity2.svg" />
              <SmallTitle>Access young talent</SmallTitle>
            </div>
            <Text>
              Connect to a vast source of fresh thinking and motivated students
              across the globe who could be candidates for future recruitment.
            </Text>
          </Opportunity>
          <Opportunity>
            <div>
              <OpportunityIcon icon="/images/opportunity3.svg" />
              <SmallTitle>Create Impact</SmallTitle>
            </div>
            <Text>
              Support further development of innovative solutions which solve
              environmental sustainability problems.
            </Text>
          </Opportunity>
          <Opportunity>
            <div>
              <OpportunityIcon icon="/images/opportunity4.svg" />
              <SmallTitle>Increase brand visibility</SmallTitle>
            </div>
            <Text>
              Increase awareness of your organization, programs, and cause.
            </Text>
          </Opportunity>
        </OpportunitiesWrapper>
        <div
          style={{
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto',
            padding: isMobile ? '0 20px' : '0',
          }}
        >
          <Label>INNOVATION</Label>
          <SecondaryTitle>Ways to Partner</SecondaryTitle>
          <Text color="black" align="center">
            Discover how we engage with corporations to open doors to new
            opportunities and innovation for our young talents.
          </Text>
        </div>
        <WaysWrapper>
          <Way image="/images/ways1.webp">
            <h3>Internships</h3>
            <Link href="/partner/internships">
              <div>
                Learn more&nbsp;&nbsp;&nbsp;&nbsp;
                <FontAwesomeIcon icon={faArrowRight as IconProp} />
              </div>
            </Link>
          </Way>
          <Way image="/images/ways2.webp">
            <h3>Masterclasses</h3>
            <Link href="/partner/masterclasses">
              <div>
                Learn more&nbsp;&nbsp;&nbsp;&nbsp;
                <FontAwesomeIcon icon={faArrowRight as IconProp} />
              </div>
            </Link>
          </Way>
          <Way image="/images/ways3.webp">
            <h3>Sponsoring</h3>
            <Link href="/partner/sponsoring">
              <div>
                Learn more&nbsp;&nbsp;&nbsp;&nbsp;
                <FontAwesomeIcon icon={faArrowRight as IconProp} />
              </div>
            </Link>
          </Way>
        </WaysWrapper>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '70px',
          }}
        >
          <Link href="/become-partner">
            <ActionButton background="black" color="white">
              Contact Us
            </ActionButton>
          </Link>
        </div>
        <GreyWrapper>
          <div style={{ textAlign: 'center' }}>
            <Label>ABOUT</Label>
            <SecondaryTitle>About The Earth Prize</SecondaryTitle>
            <Text align="center" color="black">
              Through The Earth Prize competition students are inspired,
              educated, mentored and empowered to solve environmental
              sustainability issues. We do this by giving access to:
            </Text>
            <SectionsWrapper>
              <Section
                image="/images/about1.svg"
                position="right -80px top -65px"
              >
                <SmallTitle>
                  A global network of inspirational individuals
                </SmallTitle>
              </Section>
              <Section
                image="/images/about2.svg"
                position="right -80px top -20px"
              >
                <SmallTitle>Mentoring by university students</SmallTitle>
              </Section>
              <Section
                image="/images/about3.svg"
                position="right -80px top -45px"
              >
                <SmallTitle>Exclusive online learning content</SmallTitle>
              </Section>
              <Section
                image="/images/about4.svg"
                position="right -40px top -20px"
              >
                <SmallTitle>Entrepreneurial skills training</SmallTitle>
              </Section>
            </SectionsWrapper>
          </div>
        </GreyWrapper>
        <TestimonialsWrapper>
          <Label>TESTIMONIALS</Label>
          <br />
          <CounterWrapper>
            {isMobile && (
              <FontAwesomeIcon
                onClick={
                  activeTestimonial === 0
                    ? () => setActiveTestimonial(testimonials.length - 1)
                    : () => setActiveTestimonial(activeTestimonial - 1)
                }
                icon={faArrowLeft as IconProp}
              />
            )}
            <Label>
              {activeTestimonial + 1} |{' '}
              <span style={{ fontWeight: 400 }}>{testimonials.length}</span>
            </Label>
            {isMobile && (
              <FontAwesomeIcon
                onClick={
                  activeTestimonial === testimonials.length - 1
                    ? () => setActiveTestimonial(0)
                    : () => setActiveTestimonial(activeTestimonial + 1)
                }
                icon={faArrowRight as IconProp}
              />
            )}
          </CounterWrapper>
          <Slider>
            {!isMobile && (
              <FontAwesomeIcon
                onClick={
                  activeTestimonial === 0
                    ? () => setActiveTestimonial(testimonials.length - 1)
                    : () => setActiveTestimonial(activeTestimonial - 1)
                }
                icon={faArrowLeft as IconProp}
              />
            )}
            <h3>&quot;{testimonials[activeTestimonial].text}&quot;</h3>
            {!isMobile && (
              <FontAwesomeIcon
                onClick={
                  activeTestimonial === testimonials.length - 1
                    ? () => setActiveTestimonial(0)
                    : () => setActiveTestimonial(activeTestimonial + 1)
                }
                icon={faArrowRight as IconProp}
              />
            )}
          </Slider>
          <Text color="black">
            <b>{testimonials[activeTestimonial].author}</b>
          </Text>
          <Text color="black">{testimonials[activeTestimonial].position}</Text>
        </TestimonialsWrapper>
        <GreyWrapper>
          <div
            style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}
          >
            <Label>PARTNERSHIPS</Label>
            <SecondaryTitle>Our Partners</SecondaryTitle>
            <Text color="black" align="center">
              Below are some of our partners which champion&nbsp;
              <Link href="https://www.earth-foundation.org">
                The Earth Foundation
              </Link>
              &nbsp;and The Earth Prize
            </Text>
          </div>
          <PartnersWrapper>
            <PartnerBox image="/images/partner1.png" />
            <PartnerBox image="/images/partner2.png" />
            <PartnerBox image="/images/partner3.png" />
            <PartnerBox image="/images/partner4.png" />
            <PartnerBox image="/images/partner5.webp" />
          </PartnersWrapper>
        </GreyWrapper>
        <Contact>
          <Label>SAY HELLO</Label>
          <SecondaryTitle>Get in touch</SecondaryTitle>
          <Text>
            Become part of The Earth Prize ecosystem and help us empower young
            talents around the world to drive positive impact.
          </Text>
          <SmallTitle>Connect with our partnership team!</SmallTitle>
          <Link href="/become-partner">
            <ActionButton onClick={() => {}} color="white">
              Contact Us
            </ActionButton>
          </Link>
        </Contact>
      </BecomePartnerWrapper>
      <Footer fullHeight />
    </Layout>
  );
};

const BecomePartnerWrapper = styled.div`
  background-color: white;
  position: relative;
  color: black;
`;

const Quote = styled.div`
  ${({ theme: { Consts } }) => css`
    position: absolute;
    top: 400px;
    left: 10%;
    height: 810px;
    width: 650px;
    background-color: white;
    background-image: url('/images/partnersBackground.svg');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    color: black;
    padding: 126px 161px 0 146px;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      left: 5%;
      height: 610px;
      width: 500px;
      top: 410px;
      padding: 86px 85px 0 95px;
    }

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      left: 5%;
      height: 610px;
      width: 500px;
      top: 320px;
      padding: 86px 85px 0 95px;
    }
  `}
`;

const GreyWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    background-color: #f7f8fa;
    color: black;
    padding: 110px 130px;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      padding: 110px 20px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 50px 20px;
    }
  `}
`;

const TestimonialsWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    padding: 150px 130px;
    display: flex;
    align-items: center;
    flex-direction: column;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 50px 20px;
    }
  `}
`;

const Contact = styled.div`
  ${({ theme: { Consts } }) => css`
    text-align: center;
    background-color: black;
    color: white;
    padding: 110px 130px;
    display: flex;
    align-items: center;
    flex-direction: column;

    > p {
      max-width: 750px;
      text-align: center;
      margin: 0 auto 50px;
    }

    > h3 {
      margin-bottom: 55px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 50px 20px;
    }
  `}
`;

const FlexWrapperStyled = styled(FlexWrapper)`
  ${({ theme: { Consts } }) => css`
    padding-top: 80px;
    height: 1000px;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      height: 800px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      height: 800px;
      grid-template-rows: 1fr 1fr;
    }
  `}
`;

const Label = styled.p`
  ${({ theme: { Consts } }) => css`
    color: #00b7bd;
    font-weight: 900;
    font-size: 15px;
    text-transform: uppercase;
    letter-spacing: 0.315em;
    margin: 0 0 23px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 11px;
      letter-spacing: 0.25em;
      margin: 0 0 12px;
    }
  `}
`;

const MainTitle = styled.h1`
  ${({ theme: { Consts } }) => css`
    font-weight: 900;
    font-size: 52px;
    line-height: 80px;
    margin: 0 0 74px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 30px;
      line-height: 41px;
      margin: 0 0 40px;
    }
  `}
`;

const SecondaryTitle = styled.h2`
  ${({ theme: { Consts } }) => css`
    font-weight: 900;
    font-size: 40px;
    line-height: 55px;
    margin: 0 0 40px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 25px;
      line-height: 34px;
    }
  `}
`;

const SmallTitle = styled.h3`
  ${({ theme: { Consts } }) => css`
    font-weight: 900;
    font-size: 20px;
    line-height: 27px;
    margin: 0 0 25px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 16px;
      line-height: 22px;
    }
  `}
`;

const PartnershipWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    text-align: center;
    max-width: 700px;
    margin: 0 auto;
    position: relative;
    padding: 0;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      margin-top: 100px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 0 20px 0;
      margin-top: 0px;
    }
  `}
`;

const OpportunitiesWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: grid;
    position: relative;
    align-items: start;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: 40px;
    padding: 0 130px;
    margin: 110px 0 130px;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      padding: 0 30px;
      grid-gap: 30px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      grid-template-columns: 1fr;
      padding: 0 20px;
      margin: 50px 0 70px;
    }
  `}
`;

const Opportunity = styled.div`
  ${({ theme: { Consts } }) => css`
    > div {
      display: block;
      align-items: center;
      margin-bottom: 0;
    }

    h3 {
      min-height: 54px;
    }

    > p {
      color: #7c7c7c;
      font-size: 16px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      > div {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
      }

      h3 {
        min-height: 0;
        margin-bottom: 0;
      }

      > p {
        padding-left: 60px;
      }
    }
  `}
`;

const OpportunityIcon = styled.div<{ icon: string }>`
  ${({ icon, theme: { Consts } }) => css`
    width: 75px;
    height: 75px;
    margin-bottom: 50px;
    background-image: ${`url(${icon})`};
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    flex-shrink: 0;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      width: 36px;
      height: 36px;
      margin-right: 23px;
      margin-bottom: 0;
    }
  `}
`;

const WaysWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: grid;
    align-items: stretch;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 46px;
    padding: 0 130px;
    margin: 80px auto 60px;
    max-width: 1400px;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      padding: 0 20px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      grid-template-columns: 1fr;
      padding: 0 20px;
      margin: 50px 0;
    }
  `}
`;

const Way = styled.div<{ image: string }>`
  ${({ image, theme: { Consts } }) => css`
    position: relative;
    height: 470px;
    background-image: ${`url(${image})`};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 13px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    padding-bottom: 41px;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background-color: rgba(0, 0, 0, 0.3);
    }

    > h3 {
      color: white;
      font-weight: 900;
      font-size: 30px;
      margin-bottom: 18px;
      position: relative;
    }

    > a {
      color: white;
      cursor: pointer;
      font-weight: 400;
      font-size: 14px;
      position: relative;
    }

    @media (max-width: ${Consts.tabletBreakPoint}) {
      height: 250px;
    }
  `}
`;

const SectionsWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: grid;
    align-items: stretch;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: 40px;
    margin: 80px 0 0;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      grid-template-columns: 1fr 1fr;
      margin: 50px 0 0;
      grid-gap: 20px;
    }
  `}
`;

const Section = styled.div<{ image: string; position: string }>`
  ${({ theme: { Consts }, image, position }) => css`
    background-color: white;
    border-radius: 10px;
    padding: 40px;
    height: 217px;
    background-image: ${`url(${image})`};
    background-size: cover;
    background-position: ${position};
    background-repeat: no-repeat;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      height: 135px;
      padding: 20px;
    }
  `}
`;

const PartnersWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: grid;
    justify-content: space-between;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-gap: 40px;
    margin-top: 120px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      grid-template-columns: 1fr 1fr;
      margin-top: 50px;
    }
  `}
`;

const PartnerBox = styled.div<{ image: string }>`
  ${({ image }) => css`
    width: 100%;
    height: 80px;
    background-image: ${`url(${image})`};
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
  `}
`;

const CounterWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 100%;
    max-width: 200px;
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    > p {
      text-align: center;
      margin-bottom: 0;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      grid-template-columns: 20px 1fr 20px;
    }
  `}
`;

const Slider = styled.div`
  ${({ theme: { Consts } }) => css`
    display: grid;
    align-items: center;
    grid-template-columns: 36px 1fr 36px;
    grid-gap: 40px;
    margin: 0 0 90px;
    justify-content: center;
    width: 100%;
    min-height: 176px;

    > h3 {
      max-width: 680px;
      text-align: center;
      font-weight: 900;
      font-size: 25px;
      line-height: 44px;
      margin: 0 auto;
      letter-spacing: 0.02em;
    }

    > svg {
      cursor: pointer;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      grid-template-columns: 1fr;
      margin: 0 0 30px;

      > h3 {
        font-size: 16px;
        line-height: 28px;
      }
    }
  `}
`;

export default Index;
